import type { ActionStep } from '../core/action-types';
import { activateNativeDocumentTextState, activateNativeText, inheritNativeDocumentText, prepareNativeTextEdit, releaseNativeDocumentText, retainNativeDocumentTextState } from './native-text';
import { throwIfAborted } from '../codecs/types';
import { prepareNativeDocumentResources } from './native-resources';
import { activateNativeImageState, nativeDocumentImage, releaseNativeDocumentImages, retainNativeImageState } from './native-images';
import { AddLayerCommand, rejected, type Command, type CommandExecution } from '../core/commands';
import { DEFAULT_LIMITS, walkLayers, type EditorDocument, type ImageLayer, type Layer } from '../core/model';
import type { DocumentSession } from '../core/session';
import { serializeProject } from '../persistence/project-format';
import { mergeLayerResources, type LayerResources } from './layer-resources';
import { activateNativeFontState, captureNativeFontState, releaseNativeDocumentFonts, type NativeFontState } from './native-fonts';

const KEY = 'maipo:public-document';

/** Layer, resource metadata and render bindings form one undoable edit. */
class AddResourceLayerCommand implements Command {
  readonly label: string;
  private readonly add: AddLayerCommand;
  private beforeMetadata: unknown;
  private beforeFonts: NativeFontState;
  private readonly beforeImages: ReturnType<typeof retainNativeImageState>;
  private readonly nextImages: ReturnType<typeof retainNativeImageState>;
  private readonly beforeText: ReturnType<typeof retainNativeDocumentTextState>;
  private readonly nextText: ReturnType<typeof retainNativeDocumentTextState>;
  private disposed = false;
  constructor(private readonly target: EditorDocument, layer: Layer, private readonly nextMetadata: unknown, private readonly nextFonts: NativeFontState, candidate: EditorDocument, parentId: string, index: number, label?: string, private readonly recordDuplicate = false) {
    this.add = new AddLayerCommand(layer, parentId, index);
    this.label = label ?? `Copy ${layer.name} with resources`;
    this.beforeMetadata = structuredClone(target.extensions[KEY]);
    this.beforeFonts = captureNativeFontState(target);
    this.beforeImages = retainNativeImageState(target);
    this.nextImages = retainNativeImageState(candidate);
    this.beforeText = retainNativeDocumentTextState(target);
    this.nextText = retainNativeDocumentTextState(candidate);
  }
  toActionStep(): ActionStep | null { return this.recordDuplicate ? { type: 'duplicate-active-layer' } : null; }
  retainedBytes(): number {
    // Count retained resource JSON and decoded font bytes conservatively.
    return this.add.retainedBytes() + this.beforeText.bytes + this.nextText.bytes + this.beforeImages.bytes + this.nextImages.bytes + 4 * (JSON.stringify(this.beforeMetadata ?? {}).length + JSON.stringify(this.nextMetadata).length);
  }
  execute(document: EditorDocument): CommandExecution {
    if (this.disposed) return rejected('Resource transfer history entry has been released.');
    if (document !== this.target) return rejected('Resource transfer belongs to another document.');
    const nextMetadata = structuredClone(this.nextMetadata);
    const result = this.add.execute(document);
    if (result.outcome !== 'changed') return result;
    try {
      activateNativeFontState(document, this.nextFonts);
      activateNativeImageState(document, this.nextImages.state);
      document.extensions[KEY] = nextMetadata;
      activateNativeDocumentTextState(document, this.nextText.state);
    } catch (error) {
      this.add.undo(document);
      if (this.beforeMetadata === undefined) delete document.extensions[KEY];
      else document.extensions[KEY] = structuredClone(this.beforeMetadata);
      activateNativeFontState(document, this.beforeFonts);
      activateNativeImageState(document, this.beforeImages.state);
      activateNativeDocumentTextState(document, this.beforeText.state);
      throw error;
    }
    return result;
  }
  undo(document: EditorDocument): void {
    const beforeMetadata = structuredClone(this.beforeMetadata);
    activateNativeFontState(document, this.beforeFonts);
    activateNativeImageState(document, this.beforeImages.state);
    this.add.undo(document);
    if (beforeMetadata === undefined) delete document.extensions[KEY];
    else document.extensions[KEY] = beforeMetadata;
    activateNativeDocumentTextState(document, this.beforeText.state);
  }
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.beforeImages.release(); this.nextImages.release(); this.beforeText.release(); this.nextText.release();
  }
  damage() { return this.add.damage(); }
}

/**
 * A layer whose resources (images, fonts, portable text) are prepared and
 * verified against a candidate of the target document, ready to commit
 * synchronously. The semantic dispatcher stages through this and commits
 * inside a transaction; `transferResourceLayer` commits at once.
 */
export interface StagedResourceLayer {
  readonly layer: Layer;
  /** True while the target still has the revision and resource table the staging saw. */
  current(target: DocumentSession, document: EditorDocument): boolean;
  /** The history command that adds the layer and switches resources, built against `document`. */
  command(document: EditorDocument, parentId: string, index: number): Command;
  /** Drops the candidate's own leases; commands built earlier keep theirs. Idempotent. */
  release(): void;
}

export interface StageResourceLayerOptions {
  signal?: AbortSignal;
  label?: string;
  fitImageToCanvas?: boolean;
  rebindPortableText?: boolean;
  recordDuplicate?: boolean;
  /** Places an image layer from its decoded size (instead of `fitImageToCanvas`). */
  place?: (image: { width: number; height: number }, document: EditorDocument) => { frame: ImageLayer['image']['frame']; transform: Layer['transform'] };
}

/** Prepare asynchronously without committing; `rejected` when the target cannot take the layer. */
export async function stageResourceLayer(source: LayerResources, target: DocumentSession, detachedLayer: Layer, options: StageResourceLayerOptions = {}): Promise<StagedResourceLayer | { rejected: string }> {
  throwIfAborted(options.signal);
  if ((options.fitImageToCanvas || options.place) && detachedLayer.kind !== 'image') throw new Error('Image placement requires an image layer.');
  const binding = target.captureRevision();
  const document = target.document;
  const incomingCount = 1 + (detachedLayer.kind === 'group' ? walkLayers(detachedLayer).length : 0);
  if (1 + walkLayers(document.root).length + incomingCount > DEFAULT_LIMITS.maxLayers) return { rejected: 'Adding this image or group would exceed the native project layer limit.' };
  const metadataBinding = JSON.stringify(document.extensions[KEY]);
  const nextMetadata = mergeLayerResources(source, document, detachedLayer, options);
  const candidate: EditorDocument = {
    ...document,
    extensions: { ...document.extensions, [KEY]: nextMetadata },
    root: { ...document.root, children: [detachedLayer, ...document.root.children] },
  };
  const controller = new AbortController();
  const abort = () => controller.abort();
  options.signal?.addEventListener('abort', abort, { once: true });
  target.closedSignal.addEventListener('abort', abort, { once: true });
  if (options.signal?.aborted || target.closedSignal.aborted) abort();
  const signal = controller.signal;
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    releaseNativeDocumentText(candidate);
    releaseNativeDocumentImages(candidate);
    releaseNativeDocumentFonts(candidate);
  };
  let staged = false;
  try {
    inheritNativeDocumentText(document, candidate);
    if (options.rebindPortableText) {
      const incoming = detachedLayer.kind === 'group' ? walkLayers(detachedLayer) : [detachedLayer];
      for (const layer of incoming) {
        if (layer.kind !== 'text' || layer.text.schemaVersion !== 2) continue;
        const result = await prepareNativeTextEdit(candidate, layer, signal);
        try { layer.text = result.text; activateNativeText(candidate, layer, result.lease.state); }
        finally { result.lease.release(); }
      }
    }
    // Verify saveability and every font dependency without modifying the target.
    await serializeProject(candidate, signal);
    await prepareNativeDocumentResources(candidate, signal);
    if ((options.fitImageToCanvas || options.place) && detachedLayer.kind === 'image') {
      const image = nativeDocumentImage(candidate, detachedLayer);
      if (options.place) {
        const placed = options.place({ width: image.width, height: image.height }, document);
        detachedLayer.image.frame = { ...placed.frame };
        detachedLayer.transform = placed.transform;
      } else {
        const scale = Math.min(1, document.width / image.width, document.height / image.height);
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        detachedLayer.image.frame = { width, height, fit: 'contain' };
        detachedLayer.transform = [1, 0, 0, 1, Math.round((document.width - width) / 2), Math.round((document.height - height) / 2)];
      }
      await serializeProject(candidate, signal);
    }
    throwIfAborted(signal);
    const current = (session: DocumentSession, live: EditorDocument) => session === target && session.isCurrent(binding) && !session.history.hasLiveCommand && JSON.stringify(live.extensions[KEY]) === metadataBinding;
    if (!current(target, document)) return { rejected: 'The destination changed while resources were prepared. Try again.' };
    const nextFonts = captureNativeFontState(candidate);
    // Detach staging ownership before a command activates its own state.
    releaseNativeDocumentFonts(candidate);
    staged = true;
    return {
      layer: detachedLayer,
      current,
      command: (live, parentId, index) => {
        if (released) throw new Error('The staged layer was released.');
        return new AddResourceLayerCommand(live, detachedLayer, nextMetadata, nextFonts, candidate, parentId, index, options.label, options.recordDuplicate);
      },
      release,
    };
  } catch (error) {
    if (target.closedSignal.aborted && !options.signal?.aborted) return { rejected: 'The destination closed while resources were prepared.' };
    throw error;
  } finally {
    options.signal?.removeEventListener('abort', abort); target.closedSignal.removeEventListener('abort', abort);
    if (!staged) release();
  }
}

/** Prepare asynchronously, then commit only to the same unchanged live session. */
export async function transferResourceLayer(source: LayerResources, target: DocumentSession, detachedLayer: Layer, parentId = 'root', index = 0, options: { signal?: AbortSignal; label?: string; fitImageToCanvas?: boolean; rebindPortableText?: boolean; recordDuplicate?: boolean } = {}): Promise<CommandExecution> {
  const staged = await stageResourceLayer(source, target, detachedLayer, options);
  if ('rejected' in staged) return rejected(staged.rejected);
  try {
    return target.history.execute(target.document, staged.command(target.document, parentId, index));
  } finally {
    staged.release();
  }
}

/** Options of the prepare/commit split below; image placement by `place` belongs to `stageResourceLayer`. */
export type ResourceLayerTransferOptions = Omit<StageResourceLayerOptions, 'place'>;

/**
 * A resource layer staged for one fixed position, for callers that commit it
 * themselves inside a transaction (`text.createPortable`). A thin view of
 * `stageResourceLayer`: it owns the staging copy's text, image and font state
 * until `release()`; `command()` builds the history entry, which retains its own.
 */
export interface PreparedResourceLayer {
  readonly target: EditorDocument;
  readonly layer: Layer;
  readonly parentId: string;
  readonly index: number;
  /** The revision the transfer was prepared against (the UI path requires it unchanged). */
  readonly binding: ReturnType<DocumentSession['captureRevision']>;
  /** The target's resource metadata is exactly as prepared. */
  matches(document: EditorDocument): boolean;
  /** Builds the history command. Callable once. */
  command(): Command;
  /** Releases the staging copy's resources. Idempotent; call after `command()` or instead of it. */
  release(): void;
}

/**
 * The asynchronous half of `transferResourceLayer` at a fixed position: plans
 * resource merges, rebinds portable text, proves the result saves and
 * prepares fonts, without touching the target or its history.
 */
export async function prepareResourceLayerTransfer(source: LayerResources, target: DocumentSession, detachedLayer: Layer, parentId = 'root', index = 0, options: ResourceLayerTransferOptions = {}): Promise<CommandExecution | PreparedResourceLayer> {
  const binding = target.captureRevision();
  const document = target.document;
  const metadataBinding = JSON.stringify(document.extensions[KEY]);
  const staged = await stageResourceLayer(source, target, detachedLayer, options);
  if ('rejected' in staged) return rejected(staged.rejected);
  let built = false;
  return {
    target: document, layer: staged.layer, parentId, index, binding,
    matches: (current) => current === document && JSON.stringify(current.extensions[KEY]) === metadataBinding,
    command: () => {
      if (built) throw new Error('Resource transfer was already committed or released.');
      built = true;
      return staged.command(document, parentId, index);
    },
    release: () => staged.release(),
  };
}

/** Commits a prepared transfer only to the same unchanged live session, then releases the staging copy. */
export function commitResourceLayerTransfer(target: DocumentSession, prepared: PreparedResourceLayer): CommandExecution {
  try {
    if (!target.isCurrent(prepared.binding) || target.history.hasLiveCommand || !prepared.matches(target.document)) return rejected('The destination changed while resources were prepared. Try again.');
    return target.history.execute(target.document, prepared.command());
  } finally { prepared.release(); }
}
